import { axios, auth } from '~/plugins/axios'

const MAX_PENDING_REQUESTS = 50

let pendingRequests = []
let authWaiters = []

const state = () => ({
  isAuth: false,
  loading: true,
  wasDefined: false,
  wasAuthenticated: false,

  needConfirmation: false,
  pendingLogin: null,
})

const getters = {
  isAuth: state => state.isAuth,
  loading: state => state.loading,
  wasDefined: state => state.wasDefined,
  wasAuthenticated: state => state.wasAuthenticated,
  needConfirmation: state => state.needConfirmation,
  pendingLogin: state => state.pendingLogin,
}

const mutations = {
  SET_IS_AUTH(state, value) {
    state.isAuth = value

    if (value) {
      state.wasAuthenticated = true
    }
  },

  SET_LOADING(state, value) {
    state.loading = value
  },

  SET_WAS_DEFINED(state, value) {
    state.wasDefined = value
  },

  SET_NEED_CONFIRMATION(state, value) {
    state.needConfirmation = value
  },

  SET_PENDING_LOGIN(state, value) {
    state.pendingLogin = value
  },
}

const actions = {
  login({ commit, dispatch }, payload) {
    commit('SET_LOADING', true)

    return auth.login(payload)
      .then(response => {
        const body = response.data?.data ?? response.data
        const twoFaRequired = body?.two_fa_required ?? Boolean(body?.message)

        if (twoFaRequired) {
          commit('SET_PENDING_LOGIN', payload.login)
          commit('SET_NEED_CONFIRMATION', true)

          return
        }

        return dispatch('loadCurrentUser')
      })
      .finally(() => {
        commit('SET_LOADING', false)
      })
  },

  verify({ commit, dispatch }, payload) {
    commit('SET_LOADING', true)

    return auth.confirm(payload.login, payload.code)
      .then(() => dispatch('loadCurrentUser'))
      .finally(() => {
        commit('SET_LOADING', false)
      })
  },

  logout({ commit, dispatch }) {
    commit('SET_LOADING', true)

    return auth.logout()
      .catch(() => {})
      .finally(() => {
        dispatch('unauthorized')
        commit('SET_NEED_CONFIRMATION', false)
        commit('SET_PENDING_LOGIN', null)
        dispatch('user/clear', null, { root: true })
        commit('SET_LOADING', false)
      })
  },

  loadCurrentUser({ commit, dispatch }) {
    return dispatch('user/loadCurrentUser', null, { root: true })
      .then(() => dispatch('authenticated'))
      .catch(error => {
        dispatch('unauthorized')

        if (error?.response?.status === 403) {
          commit('shared/SET_ERROR', {
            errors: null,
            message: error.response.data?.message || 'Доступ к данному приложению запрещен',
          }, { root: true })
        }

        throw error
      })
  },

  bootstrap({ commit, dispatch }) {
    return dispatch('loadCurrentUser')
      .catch(() => {})
      .finally(() => {
        commit('SET_LOADING', false)
        commit('SET_WAS_DEFINED', true)
      })
  },

  authenticated({ commit, dispatch }) {
    commit('SET_IS_AUTH', true)
    commit('SET_NEED_CONFIRMATION', false)
    commit('SET_PENDING_LOGIN', null)

    releaseAuthWaiters()
    dispatch('replayPendingRequests')
  },

  unauthorized({ commit }) {
    commit('SET_IS_AUTH', false)
  },

  waitForAuth({ state }) {
    if (state.isAuth) {
      return Promise.resolve()
    }

    return new Promise(resolve => {
      authWaiters.push(resolve)
    })
  },

  retryAfterAuth(context, config) {
    return new Promise((resolve, reject) => {
      if (pendingRequests.length >= MAX_PENDING_REQUESTS) {
        pendingRequests.shift()
      }

      pendingRequests.push({ config, resolve, reject })
    })
  },

  replayPendingRequests() {
    const requests = pendingRequests
    pendingRequests = []

    requests.forEach(({ config, resolve, reject }) => {
      axios.request(config).then(resolve).catch(reject)
    })
  },
}

function releaseAuthWaiters() {
  const waiters = authWaiters
  authWaiters = []

  waiters.forEach(resolve => resolve())
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
